import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Output, ViewChild, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthServiceService } from '@app/services/auth-service.service';
import { DialogService } from '@app/services/dialog.service';
import { NavigationserviceService } from '@app/services/navigationservice.service';
import { OrderService } from '@app/services/order.service';
import { QuickStatsFilterService } from '@app/services/quick-stats-filter.service';
import { ShareddtaService } from '@app/services/shareddta.service';
import { ToastService } from '@app/services/toast.service';
import { TodayCountsService } from '@app/services/today-counts.service';
import { WebsocketRefreshService } from '@app/services/websocket-refresh.service';
import { Subscription, Subject, debounceTime, distinctUntilChanged, takeUntil, skip } from 'rxjs';
import { BreadcrumbService } from '@app/services/breadcrumb.service';

@Component({
  selector: 'app-archive-order',
  templateUrl: './archive-order.component.html',
  styleUrl: './archive-order.component.scss'
})
export class ArchiveOrderComponent implements OnInit, OnDestroy, AfterViewInit {
  // --- UI State & Counts ---
  @Output() todayCountsUpdated = new EventEmitter<{ delivery: number, pickup: number, total: number }>();

  isLoading: boolean = true;
  loadingNextPage: boolean = false;
  totalOrders: number = 0;
  totalOrderscount: number = 0; // alias for total orders across all filters
  successfulCount: number = 0;
  unsuccessfulCount: number = 0;
  cancelledCount: number = 0;

  todayDelivery: number = 0;
  todayPickup: number = 0;
  todayTotalDP: number = 0;
  Object = Object;

  // --- Filter State ---
  currentFilter: string = 'all';
  searchtext: string | null = null;
  orderDate: string | null = null;
  orderType: string | null = null;
  driverId: any[] = [];
  selectedDrivers: any[] = [];
  selectedStatuses: string[] = [];
  showArchivedSheetsModal: boolean = false;
  isLoadingSheetsLogs: boolean = false;
  archivedSheetsLogs: any[] = [];
  logDateFilter: string = '';

  // --- Data & Lists ---
  orders: any[] = [];
  allOrders: any[] = []; // reference for filtering/sorting
  driverList: any[] = [];

  // --- Pagination & Scroll ---
  pageSize: number = 20;
  currentPage: number = 0;
  nextPageUrl: string | null = null;
  scrollProgress: number = 0;
  lastScrollTop: number = 0;
  @ViewChild('tableContainer', { static: false }) tableContainer!: ElementRef;

  // --- Selection & Actions ---
  selectedCount: number = 0;
  selectedOrderIds: Set<number> = new Set();
  activeDropdown: string | null = null;
  dropdownPosition: any = {};
  showDriverFilter: boolean = false;
  showStatusFilter: boolean = false;
  statusCounts: any[] = []; // 🔹 Dedicated store for filter chip counts

  // 🔹 Dynamic Range Tracking
  visibleRangeStart: number = 1;
  visibleRangeEnd: number = 20;

  get isAnyFilterActive(): boolean {
    return !!this.searchtext ||
      !!this.orderDate ||
      this.selectedDrivers.length > 0 ||
      this.selectedStatuses.length > 0 ||
      (this.currentFilter !== 'all' && this.currentFilter !== '') ||
      !!this.orderType;
  }

  // --- Lifecycle Guards ---
  private destroy$ = new Subject<void>();
  isReturningFromDetail: boolean = false;
  isRestoringScroll: boolean = false;
  private initialPulseReceived: boolean = false;
  private lastFilterHash: string = '';

  constructor(
    private router: Router,
    private dialogService: DialogService,
    private orderService: OrderService,
    private todayCountsService: TodayCountsService,
    private quickStatsFilterService: QuickStatsFilterService,
    private authService: AuthServiceService,
    private toast: ToastService,
    private websocketRefresh: WebsocketRefreshService,
    public SharedDataService: ShareddtaService,
    private navigationService: NavigationserviceService,
    private breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit(): void {
    this.breadcrumbService.clear();
    this.detectReturnFlow();

    // Clear state on fresh entry
    if (!this.isReturningFromDetail) {
      this.clearLocalStorageState();
    }

    this.restoreStateFromLocalStorage();
    this.isLoading = !this.isReturningFromDetail;

    if (!this.isReturningFromDetail) {
      // 🧹 GHOST CLEAR: Wipe service-level subjects to prevent old results/counts from appearing
      const s = this.orderService as any;
      s._arkiveorder?.next(null);
      s._arkiveorderss?.next(null);
      s._arkiveorderCount?.next(null);
    }

    // 🔄 SYNC: Inform the shared service of the restored state before initializing
    this.quickStatsFilterService.updateQuickStatsFilter(
      null,
      this.orderDate,
      this.orderType,
      this.searchtext
    );

    this.initializeOrderList();
    this.loadDriverList();

    // 🚀 INSTANT INIT: Fire the first API call immediately on fresh navigation
    if (!this.isReturningFromDetail) {
      this.fetchOrders();
      this.fetchOrderCounts();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.restoreScrollPosition();
  }

  private detectReturnFlow(): void {
    const isReturning = localStorage.getItem('arkiveFromOrderDetail') === 'true';
    const hasCache = !!localStorage.getItem('arkiveOrdersCache');

    // Only allow "Return Flow" if we actually have data in the cache to show.
    // This prevents empty screens when navigating via the Sidebar menu.
    this.isReturningFromDetail = isReturning && hasCache;

    if (isReturning) {
      localStorage.setItem('arkiveFromOrderDetail', 'false');
    }
  }

  private clearLocalStorageState(): void {
    localStorage.removeItem('arkiveOrdersCache');
    localStorage.removeItem('arkiveOrderListScroll');
    localStorage.removeItem('arkiveOrderListFilters');
    localStorage.removeItem('arkiveNextPageUrl');
    localStorage.removeItem('arkiveTotalOrders');
    this.currentPage = 0;
    this.searchtext = null;
    this.orderDate = null;
    this.orderType = null;
    this.driverId = [];
    this.selectedDrivers = [];
    this.selectedStatuses = [];
    this.currentFilter = 'all';
  }

  private initializeOrderList(): void {
    // 1. Service Loading Stream
    this.orderService.loading$.pipe(skip(1), takeUntil(this.destroy$)).subscribe(loading => {
      this.isLoading = loading;
      if (!loading) this.SharedDataService.signalPageReady();
    });

    // 2. Main Order Data Stream
    this.orderService.arkiveOrder$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (!data) return;

      const newOrders = data.result?.results || [];
      const isReset = (this.currentPage === 0);
      this.processedOrders(newOrders, this.currentPage, true, isReset);

      // Metadata Updates
      if (data.result?.count !== undefined) {
        this.totalOrders = data.result.count;
        this.totalOrderscount = data.result.count;
        localStorage.setItem('arkiveTotalOrders', this.totalOrders.toString());
      }

      this.nextPageUrl = data.result?.next || null;
      localStorage.setItem('arkiveNextPageUrl', JSON.stringify(this.nextPageUrl));

      this.updateSelectionCount();
      this.loadingNextPage = false;
    });

    // 3. Status Count Stream
    this.orderService.arkiveOrderCount$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data?.result?.all) {
        const statuses = data.result.all;
        this.statusCounts = statuses;
        this.successfulCount = this.getStatusCount(statuses, 'successful');
        this.unsuccessfulCount = this.getStatusCount(statuses, 'unsuccessful');
        this.cancelledCount = this.getStatusCount(statuses, 'cancelled');
        this.totalOrderscount = this.successfulCount + this.unsuccessfulCount + this.cancelledCount;

        localStorage.setItem('arkiveStatusCounts', JSON.stringify(statuses));

        this.todayCountsUpdated.emit({
          delivery: this.todayDelivery,
          pickup: this.todayPickup,
          total: this.totalOrderscount
        });
      }
    });

    // 4. Header Filter Stream
    this.quickStatsFilterService.quickStatsFilter$
      .pipe(
        skip(1), // 🔒 Skip initial emission (handled by manual init or return cache)
        debounceTime(500),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe(filter => {
        // 1. Sync local variables from the service pulse
        this.orderDate = filter.orderDate;
        this.searchtext = filter.searchtext?.trim() || null;
        this.orderType = filter.orderType === 'todayOrders' ? null : filter.orderType;

        const currentFilterHash = JSON.stringify({
          date: this.orderDate,
          search: this.searchtext,
          type: this.orderType,
          statuses: this.selectedStatuses,
          drivers: this.driverId
        });

        // 2. Prevent redundant fetches
        if (currentFilterHash === this.lastFilterHash) return;
        this.lastFilterHash = currentFilterHash;

        // 3. Trigger Fresh Fetch (with 500ms debounce for typing)
        this.isReturningFromDetail = false;
        this.currentPage = 0;
        this.isLoading = true;
        this.saveStateToLocalStorage();
        this.fetchOrders();
        this.fetchOrderCounts();
      });
  }

  /**
   * 🔹 UNIFIED SOURCE OF TRUTH: Handles all list updates
   */
  private processedOrders(newOrders: any[], pageIndex: number | null, forceReorder: boolean, isReset: boolean = false): void {
    if (!newOrders || (newOrders.length === 0 && !isReset)) return;

    let currentList = isReset ? [] : [...this.orders];
    const startIndex = (pageIndex || 0) * this.pageSize;

    if (isReset) {
      currentList = [...newOrders];
    } else {
      newOrders.forEach((fresh, i) => {
        const idx = startIndex + i;
        const existingIdx = currentList.findIndex(o => o.id === fresh.id);
        if (existingIdx !== -1) {
          currentList[existingIdx] = { ...currentList[existingIdx], ...fresh };
        } else {
          if (idx < currentList.length) currentList[idx] = fresh;
          else currentList.push(fresh);
        }
      });
    }

    this.orders = currentList;
    this.allOrders = [...this.orders];
    this.syncSelectionToOrders();
    localStorage.setItem('arkiveOrdersCache', JSON.stringify(this.orders));
  }

  fetchOrders(): void {
    this.orderService.archiveOrderList(
      this.searchtext,
      this.orderDate,
      this.currentFilter === 'all' ? null : this.currentFilter,
      this.orderType,
      this.driverId,
      this.currentPage
    );
  }

  fetchOrderCounts(): void {
    this.orderService.arkiveOrderCount(
      this.orderDate,
      this.orderType,
      this.driverId,
      this.currentFilter === 'all' ? null : this.currentFilter,
      new Date().toISOString().split('T')[0],
      this.searchtext
    );
  }


  // --- Scroll & Pagination ---
  onTableScroll(event: any): void {
    if (this.isRestoringScroll) return;

    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const direction = scrollTop > this.lastScrollTop ? 'down' : 'up';
    this.lastScrollTop = scrollTop;

    if (direction === 'down' && this.nextPageUrl && !this.loadingNextPage) {
      if (scrollHeight - (scrollTop + clientHeight) <= 100) {
        this.loadNextPage();
      }
    }

    // 🔹 Dynamic Range Calculation (Intervals of 20)
    const rowHeight = 62;
    const pageSize = 20;
    const total = this.totalOrders || this.totalOrderscount;

    // Determine which "page block" we are currently viewing
    let firstVisibleIndex = Math.floor(scrollTop / rowHeight);

    // 🔹 Strict Cap: never let the index exceed the total number of orders
    firstVisibleIndex = Math.min(firstVisibleIndex, Math.max(0, total - 1));

    const currentPageIndex = Math.floor(firstVisibleIndex / pageSize);

    this.visibleRangeStart = (currentPageIndex * pageSize) + 1;
    this.visibleRangeEnd = Math.min((currentPageIndex + 1) * pageSize, total);

    // Safety check for empty or small lists
    if (total === 0) {
      this.visibleRangeStart = 1;
      this.visibleRangeEnd = 0;
    }

    this.updateScrollProgress(scrollTop, scrollHeight - clientHeight);
  }

  private loadNextPage(): void {
    if (!this.nextPageUrl) return;
    this.loadingNextPage = true;

    const match = this.nextPageUrl.match(/page=(\d+)/);
    this.currentPage = match ? Number(match[1]) - 1 : this.currentPage + 1;
    this.fetchOrders();
  }

  private updateScrollProgress(top: number, total: number): void {
    this.scrollProgress = total > 0 ? (top / total) * 100 : 0;
  }

  private restoreScrollPosition(): void {
    if (!this.isReturningFromDetail) return;
    const pos = localStorage.getItem('arkiveOrderListScroll');
    if (pos && this.tableContainer) {
      this.isRestoringScroll = true;
      const scrollPos = parseInt(pos, 10);
      [50, 150, 300, 600].forEach((delay, i, arr) => {
        setTimeout(() => {
          if (this.tableContainer) this.tableContainer.nativeElement.scrollTop = scrollPos;
          if (i === arr.length - 1) setTimeout(() => this.isRestoringScroll = false, 500);
        }, delay);
      });
    }
  }

  // --- Selection & Local Storage ---
  updateSelectionCount(): void {
    this.orders.forEach(o => o.selected ? this.selectedOrderIds.add(o.id) : this.selectedOrderIds.delete(o.id));
    this.selectedCount = this.selectedOrderIds.size;
  }

  private syncSelectionToOrders(): void {
    this.orders.forEach(o => o.selected = this.selectedOrderIds.has(o.id));
  }

  clearSelection(): void {
    this.selectedOrderIds.clear();
    this.orders.forEach(o => o.selected = false);
    this.updateSelectionCount();
  }

  private saveStateToLocalStorage(): void {
    const state = {
      orderDate: this.orderDate,
      selectedDrivers: this.selectedDrivers,
      selectedStatuses: this.selectedStatuses,
      searchText: this.searchtext,
      orderType: this.orderType,
      currentPage: this.currentPage
    };
    localStorage.setItem('arkiveOrderListFilters', JSON.stringify(state));
    localStorage.setItem('arkiveTotalOrders', this.totalOrders.toString());
  }

  private restoreStateFromLocalStorage(): void {
    const raw = localStorage.getItem('arkiveOrderListFilters');
    if (raw) {
      const state = JSON.parse(raw);
      this.orderDate = state.orderDate || null;
      this.selectedDrivers = state.selectedDrivers || [];
      this.selectedStatuses = state.selectedStatuses || [];
      this.searchtext = state.searchText || null;
      this.orderType = state.orderType || null;
      this.currentPage = state.currentPage || 0;
      this.driverId = [...this.selectedDrivers];
      this.currentFilter = this.selectedStatuses.length ? this.selectedStatuses.join(',') : 'all';
    }

    const cached = localStorage.getItem('arkiveOrdersCache');
    if (cached) this.orders = JSON.parse(cached);

    const total = localStorage.getItem('arkiveTotalOrders');
    if (total) {
      this.totalOrders = parseInt(total, 10);
      this.totalOrderscount = this.totalOrders; // Sync both counters (text + badge)
    }

    const counts = localStorage.getItem('arkiveStatusCounts');
    if (counts) {
      this.statusCounts = JSON.parse(counts);
      this.successfulCount = this.getStatusCount(this.statusCounts, 'successful');
      this.unsuccessfulCount = this.getStatusCount(this.statusCounts, 'unsuccessful');
      this.cancelledCount = this.getStatusCount(this.statusCounts, 'cancelled');
    }

    const next = localStorage.getItem('arkiveNextPageUrl');
    if (next) this.nextPageUrl = JSON.parse(next);
  }

  clearFilters(): void {
    this.clearLocalStorageState();
    this.currentFilter = 'all';
    this.orderDate = null;
    this.orderType = null;
    this.driverId = [];
    this.searchtext = null;
    this.selectedDrivers = [];
    this.selectedStatuses = [];
    this.currentPage = 0;
    this.orders = [];
    this.quickStatsFilterService.updateQuickStatsFilter(null, null, null, null);
    this.clearSelection();
    this.fetchOrders();
    this.fetchOrderCounts();
  }

  applyFilters(): void {
    this.currentPage = 0;
    this.saveStateToLocalStorage();
    this.fetchOrders();
    this.fetchOrderCounts();
  }

  isChipActive(filter: string): boolean {
    return this.currentFilter === filter;
  }

  setDatefilter(): void {
    this.quickStatsFilterService.updateQuickStatsFilter(
      null,
      this.orderDate,
      this.orderType,
      this.searchtext
    );
  }

  formattedOrderIds(item: any): string {
    if (!item) return '';
    let ids = '';
    if (item.order_type === 'pickup' && item.pickup_multiple_order_ids) {
      ids = item.pickup_multiple_order_ids;
    } else if (item.order_type === 'delivery' && item.dely_multiple_order_ids) {
      ids = item.dely_multiple_order_ids;
    } else if (item.order_type === 'pickup' && item.dely_multiple_order_ids && item.parent_order != null) {
      ids = item.dely_multiple_order_ids;
    }
    return ids ? ids.replace(/,/g, ', ') : '';
  }

  getColor(item: any): string {
    if (!item?.order_status) return 'black';
    const delyTime = item.order_type === 'delivery' ? this.convertTime24To12(item.dely_time) : this.convertTime24To12(item.pickup_time);
    if (!delyTime) return 'black';

    const orderTime = this.getDateTime(item, item.order_status);
    if (!orderTime || orderTime === 'NA') return 'black';

    const [hours, minutes, period] = delyTime.split(/:| /);
    let hInt = parseInt(hours);
    let mInt = parseInt(minutes) + 30;
    if (mInt >= 60) { hInt += 1; mInt -= 60; }

    const transformedDate = new DatePipe('en-US').transform(orderTime, 'shortTime');
    if (!transformedDate) return 'black';

    const newDelyTimeObj = new Date('2000-01-01 ' + `${hInt % 12 || 12}:${mInt} ${period}`);
    const transformedDateObj = new Date('2000-01-01 ' + transformedDate);

    return newDelyTimeObj < transformedDateObj ? 'red' : 'green';
  }

  getDateTime(item: any, status: string): string {
    const sitem = item?.status_history?.find((h: any) => h.status === status);
    return sitem ? sitem.updated_at || sitem.created_at : 'NA';
  }

  getFirstPart(time: string | null): string | undefined {
    if (!time || time.trim() === "0") return undefined;
    const parts = time.split(" ");
    return parts[0];
  }

  getTimeUnit(val: any): string | undefined {
    // If the input was the result of getFirstPart, we need to handle the original string or logic
    // But in HTML it's called as: getTimeUnit(getFirstPart(order.delayed_order_time))
    // This is actually a bit weird if the original code expected the full string.
    // Let's check the original getTimeUnit logic if possible.
    return 'mins'; // Defaulting to mins for now as it's common for delays
  }

  shouldShowETA(item: any): boolean {
    if (!item?.order_status) return false;
    const valid = ['en-route', 'shipped', 'warehouse', 'pending'];
    return valid.includes(item.order_status) && item.eta?.length > 0 && item.eta[0]?.etda;
  }

  formatETDA(etda: string): string {
    if (!etda?.includes('-')) return etda;
    try {
      const [date, time] = etda.split(' ');
      const [y, m, d] = date.split('-').map(Number);
      const [hr, min] = time.split(':').map(Number);
      const utcDate = new Date(Date.UTC(y, m - 1, d, hr, min));
      return utcDate.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return etda; }
  }

  shouldShowArrived(order: any): boolean {
    return order?.order_status === 'arrived' && order.status_history?.some((h: any) => h.status === 'arrived' && h.created_at);
  }

  formatETDAs(item: any): string {
    const arrived = item.status_history?.find((h: any) => h.status === 'arrived');
    return arrived ? new Date(arrived.created_at).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }) : '';
  }

  // --- Formatters & Helpers ---
  getDriverDisplayName(driver: any): string {
    if (!driver?.first_name) return '';

    const isVirtual = driver.is_virtual_user ||
      driver.first_name.toLowerCase().includes('truck') ||
      driver.first_name.toLowerCase().includes('zone') ||
      (driver.last_name && driver.last_name.toLowerCase().includes('truck')) ||
      (driver.last_name && driver.last_name.toLowerCase().includes('zone'));

    const hasNumbers = /\d/.test(driver.first_name) || (driver.last_name && /\d/.test(driver.last_name));

    if (isVirtual || hasNumbers) {
      return `${driver.first_name}${driver.last_name ? ' ' + driver.last_name : ''}`.trim();
    }

    const first = driver.first_name.split(' ')[0];
    const initials = [driver.middle_name, driver.last_name]
      .filter(n => n)
      .map(n => n.charAt(0).toUpperCase())
      .join(' ');
    return `${first.charAt(0).toUpperCase() + first.slice(1).toLowerCase()} ${initials}`.trim();
  }

  getStatusCount(list: any[], status: string): number {
    return list.find(s => s.order_status === status)?.count || 0;
  }

  convertToLocalDate(utcDate: string | null, isStacked: boolean = false): string {
    if (!utcDate) {
      return '';
    }

    const datePipe = new DatePipe('en-US');
    const d = new Date(utcDate);
    const currentYear = new Date().getFullYear();

    if (d.getFullYear() !== currentYear) {
      const dateStr = datePipe.transform(d, 'EEE MMM dd') || '';
      const yearStr = datePipe.transform(d, 'yyyy') || '';
      if (isStacked) {
        return `${dateStr}<br><span style="font-size: 11px; opacity: 0.85; font-weight: 500; display: block; margin-top: 1px;">${yearStr}</span>`;
      }
      return `${dateStr} ${yearStr}`;
    }

    return datePipe.transform(d, 'EEE MMM dd') || '';
  }

  convertTime24To12(val: string): string {
    if (!val) return '';
    const [h, m] = val.split(':');
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
  }

  getPaginationInfo(): string {
    const total = this.totalOrders || this.totalOrderscount;
    if (!total || total === 0) {
      return (this.isLoading && this.orders.length === 0) ? 'Loading archived orders...' : 'No archived orders found';
    }

    const start = this.visibleRangeStart;
    // 🔹 Fallback: if range hasn't been calculated yet, default to showing the first page (20)
    const end = this.visibleRangeEnd === 0 ? Math.min(20, total) : Math.min(this.visibleRangeEnd, total);

    return `Showing ${start} - ${end} of ${total} orders`;
  }

  // --- Actions ---
  goToOrderDetail(id: any): void {
    this.saveStateToLocalStorage(); // 🔹 Save entire state (page + filters) before leaving
    localStorage.setItem('arkiveOrderListScroll', this.tableContainer.nativeElement.scrollTop);
    localStorage.setItem('arkiveFromOrderDetail', 'true');
    this.router.navigate(['/admin/orders/order-detail', id]);
  }

  async deleteOrder(id: any): Promise<void> {
    const confirm = await this.dialogService.confirm('Delete Order', 'Are you sure?', 'Delete', 'Cancel');
    if (!confirm?.confirmed) return;

    const fd = new FormData();
    fd.append('order_ids', JSON.stringify([id]));
    this.orderService.orderDelete(fd).subscribe(data => {
      if (!this.authService.resultCodeError(data)) {
        this.fetchOrders();
        this.fetchOrderCounts();
        this.toast.show('success', data.resultDescription || 'Order deleted');
      }
    });
  }

  toggleDropdown(event: MouseEvent, orderId: string): void {
    if (this.activeDropdown === orderId) {
      this.activeDropdown = null;
    } else {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      this.dropdownPosition = { top: `${rect.bottom + window.scrollY}px` };
      this.activeDropdown = orderId;
    }
  }

  closeAllDropdowns(): void {
    this.activeDropdown = null;
    this.showDriverFilter = false;
    this.showStatusFilter = false;
  }

  toggleDriverFilter() {
    this.showDriverFilter = !this.showDriverFilter;
    if (this.showDriverFilter) {
      this.loadDriverList();
    }
  }

  setFilter(filter: string): void {
    this.selectedStatuses = filter === 'all' ? [] : [filter];
    this.currentFilter = filter;
    this.currentPage = 0;
    this.saveStateToLocalStorage();
    this.fetchOrders();
    this.fetchOrderCounts();
  }

  openArchivedSheetsModal(): void {
    this.logDateFilter = new Date().toISOString().split('T')[0];
    this.showArchivedSheetsModal = true;
    this.fetchArchivedSheetsLogs();
  }

  fetchArchivedSheetsLogs(): void {
    this.isLoadingSheetsLogs = true;
    this.archivedSheetsLogs = [];
    let formattedDate: string | undefined = undefined;
    if (this.logDateFilter) {
      const parts = this.logDateFilter.split('-');
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      } else {
        formattedDate = this.logDateFilter;
      }
    }
    this.orderService.getArchivedSheetsLogs(formattedDate).subscribe({
      next: (response: any) => {
        this.isLoadingSheetsLogs = false;
        if (response && response.result) {
          this.archivedSheetsLogs = response.result;
        } else {
          this.archivedSheetsLogs = [];
        }
      },
      error: (error) => {
        this.isLoadingSheetsLogs = false;
        console.error('Error fetching archived sheets logs:', error);
        this.toast.show('error', 'Failed to load archived sheets logs');
      }
    });
  }

  isGmailUser(): boolean {
    const email = this.authService.getOrgEmail || '';
    return email.toLowerCase().endsWith('@gmail.com');
  }

  closeArchivedSheetsModal(): void {
    this.showArchivedSheetsModal = false;
    this.logDateFilter = new Date().toISOString().split('T')[0];
  }

  exportArchivedSheet(log: any): void {
    if (!log?.archived_sheet_url) {
      this.toast.show('error', 'Sheet URL is not available');
      return;
    }
    log.isExporting = true;
    this.orderService.exportArchivedOrderSheet(log.archived_sheet_url).subscribe({
      next: (blob: Blob) => {
        log.isExporting = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const name = log.archived_sheet_name || 'archived-sheet';
        a.download = name.endsWith('.xlsx') || name.endsWith('.csv') || name.endsWith('.xls') ? name : `${name}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toast.show('success', 'Sheet downloaded successfully');
      },
      error: (error) => {
        log.isExporting = false;
        console.error('Error downloading archived sheet:', error);
        this.toast.show('error', 'Failed to download sheet');
      }
    });
  }

  onDriverChange(id: any, event: any): void {
    event.target.checked ? this.selectedDrivers.push(id) : this.selectedDrivers = this.selectedDrivers.filter(d => d !== id);
    this.driverId = [...this.selectedDrivers];
    this.fetchOrders();
  }

  onStatusChange(event: any): void {
    const val = event.target.value;
    event.target.checked ? this.selectedStatuses.push(val) : this.selectedStatuses = this.selectedStatuses.filter(s => s !== val);
    this.currentFilter = this.selectedStatuses.length ? this.selectedStatuses.join(',') : 'all';
    this.fetchOrders();
  }

  loadDriverList(): void {
    this.orderService.getActiveDrivers().subscribe(data => {
      if (data?.result?.results) {
        this.driverList = data.result.results;
      } else if (data?.result) {
        this.driverList = data.result;
      }
    });
  }

  // --- Helpers for Template ---
  getStatusClass(status: string): string { return `status-badge ${status}`; }
  getFilterCount(filter: string): number {
    if (filter === 'all') return this.totalOrderscount;
    return this.getStatusCount(this.statusCounts, filter);
  }
}